import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalDocumentsService } from "services/ClientPortalDocumentsService";
import {
  ClientPortalDocumentFiltersModel,
  ClientPortalDocumentInstanceModel,
  ClientPortalDocumentModel,
  DocumentAttachmentModel,
  IntSelectModel,
} from "services/api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

type DocumentsTableProps = {
  Documents: ClientPortalDocumentModel[];
  DocumentAttachments: DocumentAttachmentModel[];
  DocumentInstances: ClientPortalDocumentInstanceModel[];
  IncludeArchived: boolean;
  LoadingDocuments: boolean;
  LoadingAttachments: boolean;
  LoadingInstances: boolean;
  DocumentTypes: IntSelectModel[];
  TypesLastUpdateTime: Date;
};

const initialState: DocumentsTableProps = {
  Documents: new Array<ClientPortalDocumentModel>(),
  DocumentAttachments: new Array<DocumentAttachmentModel>(),
  DocumentInstances: new Array<ClientPortalDocumentInstanceModel>(),
  IncludeArchived: false,
  LoadingDocuments: false,
  LoadingAttachments: false,
  LoadingInstances: false,
  DocumentTypes: new Array<IntSelectModel>(),
  TypesLastUpdateTime: new Date(),
};

export const DocumentsTableSlice = createSlice({
  name: "Documents",
  initialState,
  reducers: {
    setIncludeArchived: (state, action) => {
      state.IncludeArchived = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDocuments.pending, (state) => {
        state.LoadingDocuments = true;
      })
      .addCase(getDocuments.fulfilled, (state, action) => {
        state.Documents = action.payload;
        state.LoadingDocuments = false;
      })
      .addCase(getDocuments.rejected, (state) => {
        state.Documents = new Array<ClientPortalDocumentModel>();
        state.LoadingDocuments = false;
      })
      .addCase(getDocumentAttachments.pending, (state) => {
        state.LoadingAttachments = true;
      })
      .addCase(getDocumentAttachments.fulfilled, (state, action) => {
        state.DocumentAttachments = action.payload;
        state.LoadingAttachments = false;
      })
      .addCase(getDocumentAttachments.rejected, (state) => {
        state.DocumentAttachments = new Array<DocumentAttachmentModel>();
        state.LoadingAttachments = false;
      })
      .addCase(getDocumentInstances.pending, (state) => {
        state.LoadingInstances = true;
      })
      .addCase(getDocumentInstances.fulfilled, (state, action) => {
        state.DocumentInstances = action.payload;
        state.LoadingInstances = false;
      })
      .addCase(getDocumentInstances.rejected, (state) => {
        state.DocumentInstances = new Array<ClientPortalDocumentInstanceModel>();
        state.LoadingInstances = false;
      })
      .addCase(getDocumentTypes.pending, (state) => {
        state.LoadingDocuments = true;
      })
      .addCase(getDocumentTypes.fulfilled, (state, action) => {
        state.DocumentTypes = action.payload;
        state.LoadingDocuments = false;
        state.TypesLastUpdateTime = new Date();
      })
      .addCase(getDocumentTypes.rejected, (state) => {
        state.DocumentInstances = new Array<ClientPortalDocumentInstanceModel>();
        state.LoadingDocuments = false;
        state.TypesLastUpdateTime = new Date();
      });
  },
});

export const getDocuments = createAsyncThunk<ClientPortalDocumentModel[], ClientPortalDocumentFiltersModel>(
  "Documents/GetDocuments",
  async (filterModel: ClientPortalDocumentFiltersModel) => {
    const documentsService = container.get<IClientPortalDocumentsService>(SERVICE_TYPES.ClientPortalDocumentsService);
    return await documentsService.getDocuments(filterModel);
  }
);

export const getDocumentInstances = createAsyncThunk<ClientPortalDocumentInstanceModel[], string>(
  "Documents/GetDocumentInstances",
  async (documentID: string) => {
    const documentsService = container.get<IClientPortalDocumentsService>(SERVICE_TYPES.ClientPortalDocumentsService);
    return await documentsService.getDocumentInstances(documentID);
  }
);

export const getDocumentAttachments = createAsyncThunk<DocumentAttachmentModel[], string>(
  "Documents/GetDocumentAttachments",
  async (documentID: string) => {
    const documentsService = container.get<IClientPortalDocumentsService>(SERVICE_TYPES.ClientPortalDocumentsService);
    return await documentsService.getDocumentAttachments(documentID);
  }
);

export const downloadAttachmentFile = createAsyncThunk(
  "Documents/DownloadAttachmentFile",
  async (attachmentID: number) => {
    const documentsService = container.get<IClientPortalDocumentsService>(SERVICE_TYPES.ClientPortalDocumentsService);
    return await documentsService.downloadAttachmentFile(attachmentID);
  }
);

export const downloadDocumentFile = createAsyncThunk(
  "Documents/DownloadDocumentFile",
  async ({ documentID, filePath }: { documentID: string; filePath?: string }) => {
    const documentsService = container.get<IClientPortalDocumentsService>(SERVICE_TYPES.ClientPortalDocumentsService);
    return await documentsService.downloadDocumentFile(documentID, filePath || "");
  }
);

export const getDocumentTypes = createAsyncThunk("Documents/GetDocumentTypes", async () => {
  const documentsService = container.get<IClientPortalDocumentsService>(SERVICE_TYPES.ClientPortalDocumentsService);
  return await documentsService.getDocumentTypes();
});

export const { setIncludeArchived } = DocumentsTableSlice.actions;
export const selectDocuments = (state: RootState) => state.documentsTable.Documents;
export const selectAttachments = (state: RootState) => state.documentsTable.DocumentAttachments;
export const selectInstances = (state: RootState) => state.documentsTable.DocumentInstances;
export const selectIncludeArchived = (state: RootState) => state.documentsTable.IncludeArchived;
export const selectLoadingDocuments = (state: RootState) => state.documentsTable.LoadingDocuments;
export const selectLoadingAttachments = (state: RootState) => state.documentsTable.LoadingAttachments;
export const selectLoadingInstances = (state: RootState) => state.documentsTable.LoadingInstances;
export const selectDocumentTypes = (state: RootState) => state.documentsTable.DocumentTypes;
export const selectTypesLastUpdateTime = (state: RootState) => state.documentsTable.TypesLastUpdateTime;

export default DocumentsTableSlice.reducer;
